/* src/components/TransactionTable.jsx */

import React, { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Компонент таблицы транзакций с:
 * - бесконечной прокруткой;
 * - навигацией с клавиатуры (стрелки + Enter);
 * - подсветкой строки при наведении мыши (только при движении);
 * - разворачиванием строки для показа дополнительных данных;
 * - автопрокруткой к выбранной строке.
 */
export default function TransactionTable({
  data,
  total,
  limit,
  offset,
  onLoadMore,
  onRowSelect,
}) {
  const containerRef = useRef(null)

  const [highlighted, setHighlighted] = useState(0)           // Индекс строки, выделенной клавиатурой
  const [selectedIndex, setSelectedIndex] = useState(null)    // Индекс выбранной (раскрытой) строки
  const [mouseMoved, setMouseMoved] = useState(false)         // Флаг, была ли активность мыши
  const mouseMoveTimeout = useRef(null)

  // === Бесконечная прокрутка: подгрузка новых данных при прокрутке ===
  const handleScroll = useCallback(() => {
    const c = containerRef.current
    if (!c) return

    // Если скролл вблизи низа — загружаем следующую порцию
    if (c.scrollHeight - c.scrollTop <= c.clientHeight + 10) {
      if (offset + limit < total) onLoadMore()
    }
  }, [offset, limit, total, onLoadMore])

  // === Клавиатурная навигация: стрелки + Enter для выбора ===
  const handleKeyDown = useCallback((e) => {
    if (!data.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (highlighted === data.length - 1 && offset + limit < total) {
        onLoadMore()
      }
      setHighlighted(h => Math.min(h + 1, data.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted(h => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const rec = data[highlighted]
      setSelectedIndex(prev => prev === highlighted ? null : highlighted)
      onRowSelect?.(rec)
    }
  }, [data, highlighted, offset, limit, total, onLoadMore, onRowSelect])

  // === Подсветка при движении мыши — включается на 150 мс ===
  useEffect(() => {
    const handleMouseMove = () => {
      setMouseMoved(true)
      clearTimeout(mouseMoveTimeout.current)
      mouseMoveTimeout.current = setTimeout(() => {
        setMouseMoved(false)
      }, 150)
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      clearTimeout(mouseMoveTimeout.current)
    }
  }, [])

  // === Установка слушателей на контейнер ===
  useEffect(() => {
    const c = containerRef.current
    if (!c) return

    c.addEventListener('scroll', handleScroll)
    c.addEventListener('keydown', handleKeyDown)

    return () => {
      c.removeEventListener('scroll', handleScroll)
      c.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleScroll, handleKeyDown])

  // === Фокус на контейнер при маунте ===
  useEffect(() => {
    containerRef.current?.focus()
  }, [])

  // === Прокрутка к выделенной строке ===
  useEffect(() => {
    const c = containerRef.current
    const row = c?.querySelectorAll('tbody tr[data-row]')[highlighted]
    if (row && c) {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [highlighted])

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      style={{
        margin: '20px auto',
        width: '80%',
        maxHeight: 400,
        overflowY: 'scroll',
        outline: 'none',
        backgroundColor: '#111',
        border: '1px solid #333',
        borderRadius: '6px',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#222', color: '#aaa' }}>
            <th style={{ padding: '8px' }}>Date,Time</th>
            <th style={{ padding: '8px' }}>+</th>
            <th style={{ padding: '8px' }}>-</th>
            <th style={{ padding: '8px' }}>Currency</th>
            <th style={{ padding: '8px' }}>Name</th>
            <th style={{ padding: '8px' }}>Respondent</th>
          </tr>
        </thead>
        <tbody>
          {data.map((tx, i) => {
            const isHighlighted = i === highlighted
            const isSelected = i === selectedIndex

            return (
              <React.Fragment key={i}>
                {/* Основная строка таблицы */}
                <tr
                  data-row
                  onClick={() => {
                    setSelectedIndex(prev => prev === i ? null : i)
                    setHighlighted(i)
                    onRowSelect?.(tx)
                  }}
                  onMouseEnter={() => {
                    if (!mouseMoved) return // Не реагировать, если мышь не двигалась
                    setHighlighted(i)
                    if (i === data.length - 1 && offset + limit < total) {
                      onLoadMore()
                    }
                  }}
                  style={{
                    backgroundColor: isSelected
                      ? '#3b82f6'
                      : isHighlighted
                      ? '#555'
                      : i % 2 === 0
                      ? '#1f1f1f'
                      : '#2a2a2a',
                    color: '#fff',
                    borderBottom: '1px solid #444',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <td style={{ padding: '8px' }}>{new Date(tx.date).toLocaleString()}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{tx.amount_get ?? '-'}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{tx.amount_send ?? '-'}</td>
                  <td style={{ padding: '8px' }}>{tx.currency}</td>
                  <td style={{ padding: '8px' }}>{tx.acc}</td>
                  <td style={{ padding: '8px' }}>{tx.resp}</td>
                </tr>

                {/* Раскрытие — доп. информация */}
                {isSelected && (
                  <tr style={{ backgroundColor: '#444', color: '#fff' }}>
                    <td colSpan={6} style={{ padding: '10px 15px' }}>
                      <div><strong>Description:</strong> {tx.description || '—'}</div>
                      <div><strong>Account:</strong> {tx.acc}</div>
                      <div><strong>Respondent:</strong> {tx.resp}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
